/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ThirdParty} from '../../../_models/third-party';
import {RemovalPoint} from '../../../_models/removal-point';
import {RemovalPointService} from '../../../_services/removal-point.service';
import {ToastrService} from 'ngx-toastr';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {AbstractControl, FormGroup, ValidationErrors} from '@angular/forms';
import {Router} from '@angular/router';
import {Helpers} from '../../../helpers';
import {AND, EQ} from '../../../_enums/operators';
import {DEFAULT, EMBEDDED} from '../../../_enums/serializer-groups';
import {I18nService} from '../../../_services/i18n.service';
import {ID, REMOVAL_POINTS_PATH} from '../../../_enums/constants';
import {ENTER} from '@angular/cdk/keycodes';
import {isControlInvalid, isControlValid, TOUCHED_OR_SUBMITTED} from '../../../_enums/form-validation';

@Component({
  selector: 'app-select-removal-points',
  templateUrl: './select-removal-points.component.html',
  styleUrls: ['./select-removal-points.component.scss']
})
export class SelectRemovalPointsComponent implements OnInit, OnChanges {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             BASE COMPONENT INPUTS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() controlName?: string;
  @Input() disableEnterKeyPress?: boolean;
  @Input() formGroup?: FormGroup;
  @Input() index?: number;
  @Input() label?: string;
  @Input() readonly ?: boolean;
  @Input() showLabel?: boolean;
  @Input() showRequired?: boolean;
  @Input() showValidation?: string;
  @Input() submitted?: boolean;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             CUSTOM COMPONENT INPUTS                                                                                ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() showManage?: boolean;
  @Input() thirdParty: ThirdParty;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             COMPONENT OUTPUTS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Output() updated = new EventEmitter<RemovalPoint>();

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public MRP_VALUE = -999;

  public data: RemovalPoint[] = [];

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(private dataService: RemovalPointService, private i18n: I18nService, private router: Router, private toastr: ToastrService) {
    this.controlName = 'removalPoint';
    this.disableEnterKeyPress = false;
    this.formGroup = null;
    this.index = 0;
    this.label = null;
    this.readonly = false;
    this.showLabel = true;
    this.showRequired = true;
    this.showValidation = TOUCHED_OR_SUBMITTED;
    this.submitted = false;

    this.showManage = true;
    this.thirdParty = null;
  }

  ngOnInit() {
    // This is intentional
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadData();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORM CONTROLS GETTERS                                                                                 ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get control(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.controlName) : null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public byId(id: number): RemovalPoint | null {
    const res = this.data.filter(v => v.id === id);
    return (0 < res.length) ? res[0] : null;
  }

  get controlId(): string {
    return `${this.controlName}_${this.index}_${ID}`;
  }

  get required(): boolean {
    return this.validators && this.validators.hasOwnProperty('required');
  }

  get showInvalid(): boolean {
    return isControlInvalid(this.control, this.showValidation, this.submitted);
  }

  get showValid(): boolean {
    return isControlValid(this.control, this.showValidation);
  }

  get validators(): ValidationErrors | null {
    return this.control && this.control.validator && this.control.validator(this.control);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              CHANGE LISTENERS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public keypress(e): void {
    if (e.keyCode === ENTER && this.disableEnterKeyPress) {
      e.preventDefault();
    }
  }

  public onChange(e): void {
    if (e && e.target && e.target.value) {
      const id = Number(e.target.value);

      // Check if option is to navigate
      if (this.MRP_VALUE === id) {
        this.router.navigate([REMOVAL_POINTS_PATH, this.thirdParty.id]);
      } else {
        this.updated.emit(this.byId(id));
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              ASYNC INITIALISATION                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private checkOnlyOne() {
    if (this.control) {
      let value = this.control.value;

      if (value && !value.id) {
        value = this.byId(value);
      }

      if (1 === this.data.length && !value) {
        value = this.data[0];
      }

      if (value) {
        this.control.setValue(value.id);
        this.updated.emit(value);
      }
    }
  }

  private loadData() {
    if (this.thirdParty) {
      this.data = [];

      this.dataService.find({
        filter: [
          ['thirdParty', EQ, this.thirdParty.id], AND,
          ['township.collectable', EQ, true], AND,
          ['headquarter', EQ, false], AND,
          ['enabled', EQ, true]
        ],
        serializerGroups: [DEFAULT, EMBEDDED]
      }).subscribe((res: HttpResponse<RemovalPoint[]>) => {
        this.data = res.body;
        this.checkOnlyOne();
      }, (err: HttpErrorResponse) => this.handleError(err));
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PRIVATE METHODS                                                                                       ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private handleError(err: HttpErrorResponse): void {
    Helpers.setLoading(false);
    this.toastr.error(`${err.status} - ${this.i18n.get(err.status)}`);
  }

}
